import { useState } from "react";
import { useModalStore } from "../../../stores/modalStore.js";

const REPORT_REASONS = [
  { value: 'spam',       label: 'Spam or unwanted advertising' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hateSpeech', label: 'Hate speech or symbols' },
  { value: 'threats',    label: 'Threats of violence' },
  { value: 'nsfw',       label: 'Explicit / NSFW content' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'illegal',    label: 'Illegal content or activities' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other',      label: 'Other' },
];

export function ReportModal() {
  const { isOpen, type, data, onClose } = useModalStore();

  const [step,    setStep]    = useState(1); // 1 = select reason, 2 = details, 3 = done
  const [reason,  setReason]  = useState('');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);

  const handleClose = () => { setStep(1); setReason(''); setDetails(''); onClose(); };

  if (!isOpen || type !== 'report') return null;

  const target   = data?.message ? 'message' : 'user';
  const targetLabel = data?.message
    ? `message by ${data.message.author || data.message.senderName || 'Unknown'}`
    : `user ${data?.user?.name || data?.user?.username || 'Unknown'}`;

  const handleSubmit = async () => {
    if (!reason) return;
    setSending(true);
    await new Promise(r => setTimeout(r, 800)); // simulate API call
    setSending(false);
    setStep(3);
  };

  if (step === 3) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={handleClose}>
        <div style={{ width: 380, background: 'var(--bg-primary,#13141f)', borderRadius: 16, padding: '40px 32px', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Report Submitted</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 24, lineHeight: 1.5 }}>
            Thank you for keeping AeThex safe. Our team will review your report within 24 hours.
          </div>
          <button onClick={handleClose}
            style={{ padding: '10px 28px', borderRadius: 10, background: 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={handleClose}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🚩 Report {target === 'user' ? 'User' : 'Message'}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Reporting {targetLabel}</div>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Step indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {[1, 2].map(s => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                  background: step >= s ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.1)', color: step >= s ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                  {s}
                </div>
                {s < 2 && <div style={{ width: 40, height: 2, background: step > s ? 'var(--color-primary,#6366f1)' : 'rgba(255,255,255,0.1)', borderRadius: 1 }} />}
              </div>
            ))}
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{step === 1 ? 'Select reason' : 'Add details'}</span>
          </div>

          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>Why are you reporting this?</div>
              {REPORT_REASONS.map(r => (
                <label key={r.value}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s',
                    background: reason === r.value ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${reason === r.value ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.06)'}` }}>
                  <input type="radio" name="reason" value={r.value} checked={reason === r.value} onChange={() => setReason(r.value)} style={{ accentColor: '#6366f1' }} />
                  <span style={{ fontSize: 13, color: reason === r.value ? '#a5b4fc' : 'rgba(255,255,255,0.7)' }}>{r.label}</span>
                </label>
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>Additional details (optional)</div>
              <textarea value={details} onChange={e => setDetails(e.target.value)} placeholder="Describe what happened…" maxLength={500}
                style={{ width: '100%', height: 100, padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'right', marginTop: 4 }}>{details.length}/500</div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={() => step === 1 ? handleClose() : setStep(1)}
            style={{ padding: '8px 18px', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step === 1 ? (
            <button onClick={() => reason && setStep(2)} disabled={!reason}
              style={{ padding: '8px 20px', borderRadius: 8, background: 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !reason ? 0.5 : 1 }}>
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={sending}
              style={{ padding: '8px 20px', borderRadius: 8, background: '#ef4444', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: sending ? 0.6 : 1 }}>
              {sending ? 'Submitting…' : 'Submit Report'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
